/**
 * @module AuthContext
 * @description Contexto global de autenticación para el sistema ACSGC (ISO 27001/27002).
 *
 * Gestiona el ciclo completo de autenticación usando Supabase Auth como proveedor de
 * identidad y `public.users` de PostgreSQL como fuente canónica de datos del usuario.
 *
 * ## Estrategia de inicialización ("Listener-First")
 * En lugar de llamar proactivamente a `getSession()` en el montaje y *además* escuchar
 * `onAuthStateChange`, el contexto usa **solo el listener** como fuente de verdad.
 * Esto elimina la race condition donde `getSession()` y el listener podían producir
 * dos actualizaciones de estado en orden impredecible, causando parpadeos de UI o
 * redirecciones dobles.
 *
 * ## Estrategia de hidratación de usuario (dos fases)
 * 1. **Inmediata (sin latencia)**: Al recibir `SIGNED_IN` / `INITIAL_SESSION`, el usuario
 *    se construye desde `session.user.user_metadata` — datos cacheados en el JWT de Auth.
 *    Esto hace que la UI cargue instantáneamente sin esperar queries a PostgreSQL.
 * 2. **Background sync**: En paralelo, se consulta `public.users` para obtener datos
 *    frescos y actualizar el estado. Cuando completa, activa `dbUserReady = true`,
 *    señal que otros contextos (OrganizationContext, DataContext) esperan para cargar.
 *
 * ## Modo Demo (sin Supabase)
 * Soporta credenciales demo para desarrollo y demos offline:
 *   - `*@minsal.cl` + `Admin1234` → usuarios jerárquicos MINSAL
 *   - `admin@company.com` + `admin123` → administrador legacy de desarrollo
 * El usuario demo se persiste en `localStorage` bajo la clave `demo-user`.
 *
 * ## Flujo de recuperación de contraseña
 * Supabase envía un link con `?code=` en la URL. Al detectar ese parámetro en el
 * montaje del componente, se activa `isRecoveryMode = true` y se evita restaurar
 * la sesión guardada, forzando al usuario a establecer una nueva contraseña.
 *
 * @see src/components/Auth/LoginForm.tsx — UI de inicio de sesión
 * @see src/lib/supabase.ts — cliente Supabase singleton
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import type { UserRow } from '../lib/database.types';
import { mockHierarchicalUsers, mockOrganizations } from '../data/mockData';

// =====================================================
// INTERFACES Y TIPOS
// =====================================================

/**
 * Representa al usuario autenticado en la aplicación.
 * Combina datos de Supabase Auth (`id`, `email`) con datos de `public.users`
 * (`role`, `organization_id`, `unit`, etc.).
 *
 * **Nota**: `organization_id` está en snake_case porque viene directamente
 * de la columna de PostgreSQL. Los demás contextos deben usar `user.organization_id`
 * (nunca `user.organizationId` — eso sería incorrecto).
 *
 * @property id                   - UUID del usuario en Supabase Auth (= UUID en public.users)
 * @property role                 - Rol del usuario: determina permisos en toda la app
 * @property organization_id      - UUID de la organización a la que pertenece (snake_case)
 * @property unit                 - Unidad/departamento dentro de la organización
 * @property force_password_change - Si `true`, el usuario debe cambiar contraseña en el próximo login
 */
interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'technical_referent' | 'auditor' | 'viewer';
  organization_id: string;
  unit: string;
  avatar_url?: string;
  is_active: boolean;
  metadata?: Record<string, unknown>;
  last_login_at?: string;
  force_password_change?: boolean;
}

/**
 * Contrato público del contexto de autenticación.
 * Todos los campos y funciones están documentados para claridad en IntelliSense.
 *
 * @property dbUserReady     - `true` cuando el background sync con `public.users` completó.
 *                             Otros contextos (Data, Organization, etc.) deben esperar este flag
 *                             antes de cargar sus datos para garantizar que `organization_id` es válido.
 * @property isRecoveryMode  - `true` cuando el usuario llegó por un link de reset de contraseña.
 *                             El componente raíz muestra el formulario de nueva contraseña en este caso.
 */
interface AuthContextType {
  /** Usuario autenticado actual (null si no hay sesión) */
  user: AuthUser | null;
  /** true mientras se verifica la sesión inicial o se procesa un login/logout */
  isLoading: boolean;
  /** Último error de autenticación, null si no hay errores */
  error: string | null;
  /** Inicia sesión con email y contraseña */
  login: (email: string, password: string) => Promise<void>;
  /** Cierra la sesión actual y limpia el estado */
  logout: () => Promise<void>;
  /** Limpia el error actual (para limpiar mensajes de error en UI) */
  clearError: () => void;
  /** Recarga los datos del usuario desde Supabase */
  refreshUser: () => Promise<void>;
  /** Actualiza nombre, unidad o avatar del usuario */
  updateProfile: (updates: Partial<Pick<AuthUser, 'name' | 'unit' | 'avatar_url'>>) => Promise<void>;
  /** Actualiza el campo JSON `metadata` del usuario (preferencias, configuración, etc.) */
  updateMetadata: (metadata: Record<string, unknown>) => Promise<void>;
  /** Cambia la contraseña y desactiva `force_password_change` */
  changePassword: (newPassword: string) => Promise<void>;
  /** Envía email de recuperación de contraseña a la dirección indicada */
  resetPassword: (email: string) => Promise<void>;
  /** true cuando `public.users` fue consultada exitosamente (background sync) */
  dbUserReady: boolean;
  /** true cuando la sesión proviene de un link de reset de contraseña */
  isRecoveryMode: boolean;
}

// =====================================================
// CONTEXTO
// =====================================================

/** Contexto React interno — consumir mediante `useAuth()` o `usePermissions()`. */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

/**
 * Proveedor del contexto de autenticación. Debe ser el ancestro más externo de la
 * aplicación para que todos los demás contextos puedan usar `useAuth()`.
 *
 * @param props.children - Árbol de componentes React que consumirán la autenticación
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // =====================================================
  // ESTADOS
  // =====================================================

  // Detectar flujo de recuperación desde la URL antes de cualquier render
  // Si la URL contiene ?code=, Supabase está procesando el token de reset de contraseña
  const isRecoveryUrl = new URLSearchParams(window.location.search).has('code');

  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Flag que indica que el background sync con public.users completó exitosamente */
  const [dbUserReady, setDbUserReady] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(isRecoveryUrl);

  // =====================================================
  // FUNCIONES DE UTILIDAD
  // =====================================================

  /** Limpia el error actual — llamado desde componentes UI tras mostrar el mensaje. */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Centraliza el manejo de errores de autenticación.
   * Extrae el mensaje del error si es una instancia de `Error`, o usa un texto genérico.
   *
   * @param error   - Error capturado (tipo `unknown` porque puede venir de catch)
   * @param context - Nombre de la operación para incluir en el log (ej: 'login', 'logout')
   */
  const handleError = useCallback((error: unknown, context: string) => {
    logger.error(`❌ [AUTH] Error en ${context}:`, error);
    const errorMessage = error instanceof Error ? error.message : `Error en ${context}`;
    setError(errorMessage);
  }, []);

  // =====================================================
  // FUNCIONES DE AUTENTICACIÓN
  // =====================================================

  /**
   * Obtiene el usuario actual desde la sesión de Supabase Auth y la tabla `public.users`.
   *
   * Implementa dos capas de timeout de 5 segundos:
   *   1. Para `getSession()` — evita colgarse si Supabase no responde
   *   2. Para la query a `public.users` — evita colgarse en queries lentas
   *
   * La función está memoizada con `useCallback` sin dependencias para garantizar
   * identidad estable de referencia y evitar que su inclusión en `useEffect` deps
   * provoque loops infinitos de re-ejecución.
   *
   * @returns `AuthUser` con datos completos de PostgreSQL, o `null` si no hay sesión
   */
  const getCurrentUser = useCallback(async (): Promise<AuthUser | null> => {
    try {
      logger.log('🔍 [AUTH] getCurrentUser: Iniciando...');

      // Timeout de seguridad para getSession() — evita bloquear indefinidamente
      const timeoutPromise = new Promise<null>((resolve) => {
        setTimeout(() => {
          logger.warn('⏱️ [AUTH] getCurrentUser: Timeout de 5s alcanzado');
          resolve(null);
        }, 5000);
      });

      const sessionPromise = supabase.auth.getSession();

      // Carrera entre la sesión y el timeout — lo que resuelva primero gana
      const result = await Promise.race([sessionPromise, timeoutPromise]);

      if (!result) {
        logger.error('❌ [AUTH] getCurrentUser: Timeout obteniendo sesión');
        return null;
      }

      const { data: { session }, error: sessionError } = result;

      if (sessionError) {
        logger.error('❌ [AUTH] getCurrentUser: Error obteniendo sesión:', sessionError);
        return null;
      }

      if (!session?.user) {
        logger.log('ℹ️ [AUTH] getCurrentUser: No hay sesión');
        return null;
      }

      logger.log('👤 [AUTH] getCurrentUser: Sesión encontrada, userId:', session.user.id);

      // Segundo timeout para la query a public.users
      const userPromise = supabase
        .from('users')
        .select(`
          id,
          organization_id,
          name,
          email,
          role,
          unit,
          avatar_url,
          metadata,
          is_active,
          last_login_at,
          force_password_change
        `)
        .eq('id', session.user.id)
        .eq('is_active', true)
        .single();

      const userTimeoutPromise = new Promise<null>((resolve) => {
        setTimeout(() => {
          logger.warn('⏱️ [AUTH] getCurrentUser: Timeout de 5s en query de usuario');
          resolve(null);
        }, 5000);
      });

      const userResult = await Promise.race([userPromise, userTimeoutPromise]);

      if (!userResult) {
        logger.error('❌ [AUTH] getCurrentUser: Timeout consultando tabla users');
        return null;
      }

      const { data: userData, error: userError } = userResult;

      if (userError) {
        logger.error('❌ [AUTH] getCurrentUser: Error obteniendo datos del usuario:', userError);
        return null;
      }

      if (!userData) {
        logger.warn('⚠️ [AUTH] getCurrentUser: Usuario no encontrado en la base de datos');
        return null;
      }

      logger.log('✅ [AUTH] getCurrentUser: Usuario obtenido:', userData.name);
      return userData as AuthUser;

    } catch (error) {
      logger.error('❌ [AUTH] getCurrentUser: Error inesperado:', error);
      return null;
    }
  }, []); // Sin dependencias — solo usa `supabase` que es un singleton estable

  /**
   * Inicia sesión con email y contraseña.
   *
   * ## Flujo para credenciales demo (`*@minsal.cl` + `Admin1234`)
   * 1. Intenta autenticar con Supabase (por si el usuario demo existe en la BD)
   * 2. Si Supabase falla o no está disponible, cae a usuario mock de `mockHierarchicalUsers`
   * 3. Persiste el usuario demo en `localStorage['demo-user']` para sobrevivir F5
   *
   * ## Flujo para credenciales reales
   * 1. `supabase.auth.signInWithPassword()` valida contra Supabase Auth
   * 2. Consulta `public.users` para obtener `role`, `organization_id`, etc.
   * 3. Actualiza `user_metadata` en el JWT de Auth para que futuras sesiones
   *    (F5, nueva pestaña) no necesiten hacer queries a PostgreSQL al cargar
   *
   * @param email    - Email del usuario (se normaliza a minúsculas)
   * @param password - Contraseña en texto plano (Supabase Auth la hashea)
   * @throws Error si las credenciales son inválidas o Supabase no está disponible
   */
  const login = useCallback(async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);

      logger.log('🔐 [AUTH] Iniciando sesión:', { email });

      if (!email.trim()) {
        throw new Error('El email es requerido');
      }

      if (!password.trim()) {
        throw new Error('La contraseña es requerida');
      }

      const emailLower = email.trim().toLowerCase();
      // Credenciales demo: correos @minsal.cl con contraseña estándar de demostración
      const isLegacyDemo = false; // Modo demo legacy desactivado
      const isHierarchicalDemo = emailLower.endsWith('@minsal.cl') && password === 'Admin1234';
      const isDemoCredentials = isLegacyDemo || isHierarchicalDemo;

      if (isDemoCredentials) {
        logger.log('🎭 [AUTH] Usando credenciales demo:', { email: emailLower, type: isHierarchicalDemo ? 'jerárquico' : 'legacy' });

        // Intentar autenticar con Supabase primero — puede que el usuario demo exista en la BD
        try {
          const { data, error: loginError } = await supabase.auth.signInWithPassword({
            email: emailLower,
            password: password,
          });

          if (!loginError && data.user) {
            // El usuario demo existe en Supabase — actualizar last_login_at y usar datos reales
            await supabase
              .from('users')
              .update({ last_login_at: new Date().toISOString() })
              .eq('id', data.user.id);

            const authUser = await getCurrentUser();

            if (authUser) {
              setUser(authUser);
              logger.log('✅ [AUTH] Login exitoso con Supabase:', { userId: authUser.id, name: authUser.name });
              return;
            }
          }
        } catch (supabaseError: unknown) {
          // Supabase no disponible — continuar silenciosamente hacia el modo demo local
          logger.warn('⚠️ [AUTH] Supabase no disponible para credenciales demo, usando modo demo');
        }

        // Limpiar cualquier error que Supabase haya seteado antes de activar modo demo
        setError(null);

        logger.log('🎭 [AUTH] Activando modo demo');

        let demoUser: AuthUser;

        if (isHierarchicalDemo) {
          // Buscar el usuario jerárquico por email en los datos mock del MINSAL
          const mockUser = mockHierarchicalUsers.find(u => u.email.toLowerCase() === emailLower);

          if (!mockUser) {
            throw new Error(`Usuario no encontrado: ${emailLower}. Usuarios disponibles: ciso.nacional@minsal.cl, director.ssmo@minsal.cl, director.ssmn@minsal.cl, ciso.hsjd@minsal.cl, ciso.hrdr@minsal.cl, auditor.hsjd@minsal.cl, referente.hsjd@minsal.cl`);
          }

          const mockOrg = mockOrganizations.find(o => o.id === mockUser.organizationId);

          demoUser = {
            id: mockUser.id,
            email: mockUser.email,
            name: mockUser.name,
            role: mockUser.role as AuthUser['role'],
            organization_id: mockUser.organizationId,
            unit: mockUser.unit,
            avatar_url: undefined,
            is_active: true,
            metadata: {
              demo: true,
              hierarchical: true,
              // dashboardLevel determina qué dashboard se muestra: 'national', 'network', 'local'
              dashboardLevel: mockUser.metadata?.dashboardLevel,
              organizationName: mockOrg?.name,
              organizationLevel: mockOrg?.level,
              organizationType: mockOrg?.orgType
            },
            last_login_at: new Date().toISOString()
          };

          logger.log('🏥 [AUTH] Usuario jerárquico demo:', {
            name: demoUser.name,
            org: mockOrg?.name,
            level: mockOrg?.level,
            dashboard: mockUser.metadata?.dashboardLevel
          });
        } else {
          // Usuario legacy — MINSAL como organización de nivel 1 para el Dashboard Nacional
          demoUser = {
            id: 'demo-admin-001',
            email: 'admin@company.com',
            name: 'Administrador Demo - MINSAL',
            role: 'admin',
            organization_id: 'a0000000-0000-0000-0000-000000000001',
            unit: 'División de Tecnologías de la Información',
            avatar_url: undefined,
            is_active: true,
            metadata: { demo: true },
            last_login_at: new Date().toISOString()
          };
        }

        // Persistir usuario demo para que sobreviva F5 y nuevas pestañas
        try {
          localStorage.setItem('demo-user', JSON.stringify(demoUser));
          logger.log('💾 [AUTH] Usuario demo guardado en localStorage');
        } catch (storageError) {
          logger.warn('⚠️ [AUTH] No se pudo guardar en localStorage:', storageError);
        }

        setUser(demoUser);
        logger.log('✅ [AUTH] Login demo exitoso:', { userId: demoUser.id, name: demoUser.name });
        return;
      }

      // ── Credenciales reales: autenticar con Supabase ──────────────────────
      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password: password,
      });

      if (loginError) {
        // Limpiar error de Supabase antes de lanzar el error personalizado
        // para que el estado no quede con dos errores simultáneos
        setError(null);
        if (loginError.message.includes('Invalid login credentials')) {
          throw new Error('Correo electrónico o contraseña incorrectos.');
        }
        throw new Error(loginError.message);
      }

      if (!data.user) {
        throw new Error('No se pudo autenticar el usuario');
      }

      logger.log('✅ [AUTH] Login exitoso, obteniendo datos del usuario...');

      // Obtener datos completos de public.users y actualizar user_metadata del JWT
      // El update de user_metadata permite que futuras sesiones (F5) se inicialicen
      // instantáneamente desde el JWT sin necesidad de queries a PostgreSQL
      try {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', data.user.id)
          .single();

        if (!userError && userData) {
          const typedUserData = userData as UserRow;
          logger.log('✅ [AUTH] Datos obtenidos de BD:', typedUserData.name);

          // Cachear datos del usuario en el JWT de Auth para futuras sesiones sin latencia
          await supabase.auth.updateUser({
            data: {
              name: typedUserData.name,
              role: typedUserData.role,
              organization_id: typedUserData.organization_id,
              unit: typedUserData.unit,
              avatar_url: typedUserData.avatar_url,
              metadata: typedUserData.metadata
            }
          });

          logger.log('✅ [AUTH] user_metadata actualizado para próximas sesiones');

          setUser(typedUserData as AuthUser);
          logger.log('✅ [AUTH] Login completado');
        } else {
          throw new Error('No se pudo obtener datos del usuario');
        }
      } catch (dbError) {
        // Fallback: construir usuario mínimo con datos del JWT de Supabase Auth
        // El usuario puede operar pero tendrá permisos/organización limitados
        logger.warn('⚠️ [AUTH] Error obteniendo datos de BD, usando fallback');
        const fallbackUser: AuthUser = {
          id: data.user.id,
          email: data.user.email || email,
          name: data.user.email?.split('@')[0] || 'Usuario',
          role: 'admin',
          organization_id: '',
          unit: 'Sin especificar',
          is_active: true,
          avatar_url: undefined,
          metadata: {},
          last_login_at: new Date().toISOString()
        };
        setUser(fallbackUser);
      }

    } catch (err) {
      handleError(err, 'login');
    } finally {
      setIsLoading(false);
    }
  }, [handleError, getCurrentUser]);

  /**
   * Cierra la sesión actual.
   * Limpia el usuario demo de `localStorage` antes de llamar a `supabase.auth.signOut()`
   * para garantizar que el modo demo no persiste entre sesiones.
   */
  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      logger.log('🚪 [AUTH] Cerrando sesión');

      // Eliminar usuario demo persistido — importante para que el login no restaure
      // automáticamente la sesión demo en el próximo `INITIAL_SESSION` sin sesión
      try {
        localStorage.removeItem('demo-user');
        logger.log('🗑️ [AUTH] Usuario demo eliminado de localStorage');
      } catch (storageError) {
        logger.warn('⚠️ [AUTH] Error limpiando localStorage:', storageError);
      }

      const { error: logoutError } = await supabase.auth.signOut();

      if (logoutError) {
        throw new Error(logoutError.message);
      }

      setUser(null);
      logger.log('✅ [AUTH] Sesión cerrada exitosamente');

    } catch (err) {
      handleError(err, 'logout');
    } finally {
      setIsLoading(false);
    }
  }, [handleError]);

  /**
   * Recarga los datos del usuario desde Supabase Auth y `public.users`.
   * Útil para reflejar cambios de perfil realizados por un administrador,
   * o para sincronizar tras un cambio de rol.
   */
  const refreshUser = useCallback(async () => {
    try {
      setIsLoading(true);

      logger.log('🔄 [AUTH] Refrescando información del usuario');

      const currentUser = await getCurrentUser();

      if (currentUser) {
        setUser(currentUser);
        logger.log('✅ [AUTH] Usuario refrescado:', { userId: currentUser.id, name: currentUser.name });
      } else {
        setUser(null);
        logger.log('ℹ️ [AUTH] No hay usuario autenticado');
      }

    } catch (err) {
      handleError(err, 'refresh user');
    } finally {
      setIsLoading(false);
    }
  }, [handleError]); // getCurrentUser es estable (sin deps), pero no lo incluimos para simplificar

  /**
   * Actualiza los campos visibles del perfil del usuario.
   * Para usuarios demo (id comienza con `demo-`), solo actualiza el estado local
   * y el `localStorage` sin intentar escribir en Supabase.
   *
   * @param updates - Objeto con los campos a actualizar (name, unit, avatar_url)
   * @throws Error si la actualización en Supabase falla
   */
  const updateProfile = useCallback(async (updates: Partial<Pick<AuthUser, 'name' | 'unit' | 'avatar_url'>>) => {
    if (!user?.id) {
      throw new Error('No hay usuario autenticado');
    }

    try {
      setIsLoading(true);
      logger.log('📝 [AUTH] Actualizando perfil del usuario:', updates);

      // Modo demo: persistir solo localmente sin tocar Supabase
      if (user.id.startsWith('demo-')) {
        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        localStorage.setItem('demo-user', JSON.stringify(updatedUser));
        logger.log('✅ [AUTH] Perfil demo actualizado localmente');
        return;
      }

      const { error } = await supabase
        .from('users')
        .update({
          name: updates.name,
          unit: updates.unit,
          avatar_url: updates.avatar_url,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      // Actualización optimista: reflejar cambios en estado local sin re-fetch
      setUser(prev => prev ? { ...prev, ...updates } : null);
      logger.log('✅ [AUTH] Perfil actualizado exitosamente');

    } catch (err) {
      handleError(err, 'actualizar perfil');
      throw err; // Re-throw para que el componente pueda mostrar el error en su UI
    } finally {
      setIsLoading(false);
    }
  }, [user, handleError]);

  /**
   * Actualiza el campo JSON `metadata` del usuario con merge superficial (shallow merge).
   * El objeto `metadata` almacena preferencias de UI, configuraciones personales y
   * cualquier dato adicional que no encaje en las columnas tipadas de `public.users`.
   *
   * @param metadata - Objeto con las claves a actualizar (se fusiona con el metadata existente)
   * @throws Error si la actualización en Supabase falla
   */
  const updateMetadata = useCallback(async (metadata: Record<string, unknown>) => {
    if (!user?.id) {
      throw new Error('No hay usuario autenticado');
    }

    try {
      setIsLoading(true);
      logger.log('📝 [AUTH] Actualizando metadata del usuario:', metadata);

      // Modo demo: persistir solo localmente
      if (user.id.startsWith('demo-')) {
        const updatedUser = {
          ...user,
          metadata: { ...user.metadata, ...metadata }
        };
        setUser(updatedUser);
        localStorage.setItem('demo-user', JSON.stringify(updatedUser));
        logger.log('✅ [AUTH] Metadata demo actualizado localmente');
        return;
      }

      // Merge superficial: preservar claves existentes, agregar/sobreescribir las nuevas
      const newMetadata = { ...user.metadata, ...metadata };

      const { error } = await supabase
        .from('users')
        .update({
          metadata: newMetadata,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setUser(prev => prev ? { ...prev, metadata: newMetadata } : null);
      logger.log('✅ [AUTH] Metadata actualizado exitosamente');

    } catch (err) {
      handleError(err, 'actualizar metadata');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user, handleError]);

  /**
   * Cambia la contraseña del usuario autenticado.
   * Requiere que el usuario ya esté autenticado (no funciona para reset sin sesión).
   *
   * Tras el cambio exitoso:
   *   - Desactiva `force_password_change` en `public.users`
   *   - Desactiva `isRecoveryMode` en el estado del contexto
   *   - Limpia el parámetro `?code=` de la URL para evitar reactivar el modo recovery en F5
   *
   * @param newPassword - Nueva contraseña (debe cumplir requisitos de seguridad de Supabase Auth)
   * @throws Error si el cambio de contraseña falla en Supabase Auth o en la actualización de BD
   */
  const changePassword = useCallback(async (newPassword: string) => {
    if (!user?.id) {
      throw new Error('No hay usuario autenticado');
    }

    try {
      setIsLoading(true);
      logger.log('🔐 [AUTH] Cambiando contraseña del usuario');

      // Cambiar contraseña en Supabase Auth (actualiza el hash en Auth, no en public.users)
      const { error: authError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (authError) throw authError;

      // Desactivar el flag de cambio forzado de contraseña en public.users
      const { error: updateError } = await supabase
        .from('users')
        .update({
          force_password_change: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Actualizar estado local y limpiar URL para evitar re-activar recovery en F5
      setUser(prev => prev ? { ...prev, force_password_change: false } : null);
      setIsRecoveryMode(false);
      window.history.replaceState(null, '', window.location.pathname);
      logger.log('✅ [AUTH] Contraseña cambiada exitosamente');

    } catch (err) {
      handleError(err, 'cambiar contraseña');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [user, handleError]);

  /**
   * Envía un email de recuperación de contraseña al usuario.
   * Supabase Auth genera un token de un solo uso y lo incluye en el enlace.
   * El enlace redirige a `{origin}/reset-password?code=...`.
   *
   * Al abrir ese link, `isRecoveryUrl` será `true` en el próximo montaje,
   * activando `isRecoveryMode` y mostrando el formulario de nueva contraseña.
   *
   * @param email - Dirección de correo del usuario que olvidó su contraseña
   * @throws Error si Supabase Auth no puede enviar el email
   */
  const resetPassword = useCallback(async (email: string) => {
    logger.log('📧 [AUTH] Solicitando reset de contraseña para:', email);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (resetError) {
      logger.error('❌ [AUTH] Error al enviar email de reset:', resetError);
      throw resetError;
    }
    logger.log('✅ [AUTH] Email de reset enviado');
  }, []);

  // =====================================================
  // EFECTOS
  // =====================================================

  /**
   * Inicializa la autenticación al montar el componente ("Listener-First Strategy").
   *
   * ## Por qué solo el listener (y no initAuth + listener)
   * El patrón clásico de React es llamar `getSession()` en `useEffect` para
   * la sesión inicial, y también suscribirse a `onAuthStateChange` para cambios.
   * Sin embargo, esto crea una race condition:
   *   - `getSession()` puede completar antes o después que `INITIAL_SESSION` dispare
   *   - Dos actualizaciones de estado competitivas → parpadeos, redirects dobles
   *
   * La solución: `onAuthStateChange` ya emite `INITIAL_SESSION` al suscribirse,
   * con la sesión actual si existe o sin sesión si no. Esto cubre exactamente
   * el caso de uso de `getSession()` sin la race condition.
   *
   * ## Timeout de seguridad (10 segundos)
   * En algunos entornos (redes lentas, Supabase lento) el evento `INITIAL_SESSION`
   * puede tardar. Si pasa el timeout sin recibir ningún evento, se asume que no hay
   * sesión activa (o se restaura el usuario demo de localStorage como fallback).
   *
   * ## Hydratación en dos fases (descripción en @module)
   * El evento `SIGNED_IN`/`INITIAL_SESSION` con sesión construye el usuario inmediatamente
   * desde `user_metadata` del JWT (sin latencia), y luego lanza un background sync a
   * `public.users` para obtener datos frescos y activar `dbUserReady`.
   */
  useEffect(() => {
    logger.log('🚀 [AUTH] Inicializando contexto de autenticación');

    let isMounted = true;
    let initialCheckTimeout: NodeJS.Timeout | null = null;

    // Suscribirse al listener de estado de autenticación — única fuente de verdad
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        logger.log('🔔 [AUTH] Evento de autenticación:', event, 'Session:', session ? 'presente' : 'null');

        // El listener respondió — cancelar el timeout de seguridad
        if (initialCheckTimeout) {
          logger.log('⏰ [AUTH] Cancelando timeout inicial');
          clearTimeout(initialCheckTimeout);
          initialCheckTimeout = null;
        }

        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
          // ── Fase 1: Hidratación inmediata desde user_metadata del JWT ────────
          // No hace queries a PostgreSQL — respuesta instantánea para la UI
          logger.log('✅ [AUTH] Usuario autenticado via listener, userId:', session.user.id);

          try {
            logger.log('🔍 [AUTH] Construyendo usuario desde user_metadata de Auth...');

            const userMetadata = session.user.user_metadata || {};

            const authUser: AuthUser = {
              id: session.user.id,
              email: session.user.email || '',
              name: userMetadata.name || session.user.email?.split('@')[0] || 'Usuario',
              role: (userMetadata.role as AuthUser['role']) || 'admin',
              organization_id: userMetadata.organization_id || '',
              unit: userMetadata.unit || 'Sin especificar',
              is_active: true,
              avatar_url: userMetadata.avatar_url || null,
              metadata: userMetadata.metadata || {},
              last_login_at: new Date().toISOString()
            };

            if (isMounted) {
              setUser(authUser);
              setIsLoading(false);
              logger.log('✅ [AUTH] Usuario construido desde Auth metadata:', {
                name: authUser.name,
                role: authUser.role,
                org_id: authUser.organization_id
              });
            }

            // ── Fase 2: Background sync con public.users ──────────────────────
            // No bloquea la UI — cuando completa, actualiza con datos frescos de PostgreSQL
            // y activa dbUserReady para que otros contextos inicien su carga
            supabase
              .from('users')
              .select('*')
              .eq('id', session.user.id)
              .single()
              .then(({ data: userData, error: userError }) => {
                if (!userError && userData && isMounted) {
                  const typedUserData = userData as UserRow;
                  logger.log('🔄 [AUTH] Sincronización con BD completada:', typedUserData.name);
                  setUser(typedUserData as AuthUser);
                  setDbUserReady(true); // Señal para OrganizationContext, DataContext, etc.
                } else if (userError) {
                  logger.warn('⚠️ [AUTH] Sincronización con BD falló (usando metadata):', userError.message);
                }
              });
          } catch (error) {
            logger.error('❌ [AUTH] Error obteniendo usuario:', error);
            if (isMounted) {
              setUser(null);
              setIsLoading(false);
            }
          }
        }
        else if (event === 'SIGNED_OUT') {
          // Usuario cerró sesión o el token expiró sin renovación
          logger.log('🚪 [AUTH] Usuario desautenticado via listener');
          if (isMounted) {
            setUser(null);
            setDbUserReady(false);
            setIsRecoveryMode(false);
            setIsLoading(false);
          }
        }
        else if (event === 'PASSWORD_RECOVERY') {
          // El usuario llegó desde un link de reset de contraseña
          // La UI mostrará el formulario de nueva contraseña en este estado
          logger.log('🔑 [AUTH] Modo de recuperación de contraseña activado');
          if (isMounted) {
            setIsRecoveryMode(true);
            setIsLoading(false);
          }
        }
        else if (event === 'TOKEN_REFRESHED') {
          // Supabase Auth renovó el JWT automáticamente
          // Durante la inicialización (timeout activo), ignorar — el flujo principal
          // ya procesará SIGNED_IN o INITIAL_SESSION con el token renovado
          if (initialCheckTimeout) {
            logger.log('🔄 [AUTH] Token refrescado (ignorando durante inicialización)');
            clearTimeout(initialCheckTimeout);
            initialCheckTimeout = null;
            return;
          }

          // Post-inicialización: el token se renovó silenciosamente — refrescar datos del usuario
          logger.log('🔄 [AUTH] Token refrescado (post-inicialización)');
          try {
            const currentUser = await getCurrentUser();
            if (currentUser && isMounted) {
              setUser(currentUser);
              logger.log('✅ [AUTH] Usuario refrescado exitosamente');
            }
          } catch (error) {
            logger.error('❌ [AUTH] Error refrescando usuario:', error);
          }
        }
        else if (event === 'INITIAL_SESSION') {
          // Este bloque solo se alcanza cuando INITIAL_SESSION llega sin sesión activa
          // (el caso con session?.user ya fue manejado arriba junto a SIGNED_IN)
          if (!session?.user) {
            logger.log('🔍 [AUTH] INITIAL_SESSION sin sesión activa');

            // Si hay ?code= en la URL, es un flujo de recovery — no restaurar sesión
            if (isRecoveryUrl) {
              logger.log('🔑 [AUTH] URL de recuperación detectada, omitiendo sesión guardada');
              if (isMounted) setIsLoading(false);
              return;
            }

            // Intentar restaurar usuario demo desde localStorage (persistido en login demo)
            try {
              const demoUserStr = localStorage.getItem('demo-user');
              if (demoUserStr) {
                const demoUser = JSON.parse(demoUserStr);
                logger.log('🎭 [AUTH] Usuario demo recuperado de localStorage:', demoUser.name);
                if (isMounted) {
                  setUser(demoUser);
                  setIsLoading(false);
                }
                return;
              }
            } catch (error) {
              logger.warn('⚠️ [AUTH] Error recuperando usuario demo:', error);
            }

            // Sin sesión ni usuario demo — mostrar pantalla de login
            if (isMounted) {
              setUser(null);
              setIsLoading(false);
            }
          }
        }
      }
    );

    // Timeout de seguridad: si el listener no responde en 10s, asumir sin sesión
    // 10s es suficiente para Supabase en redes normales; 45s era excesivo
    initialCheckTimeout = setTimeout(() => {
      if (isMounted && isLoading) {
        logger.warn('⏱️ [AUTH] Sin eventos de auth en 10s, asumiendo sin sesión');

        // Último intento: restaurar usuario demo antes de mostrar login
        try {
          const demoUserStr = localStorage.getItem('demo-user');
          if (demoUserStr) {
            const demoUser = JSON.parse(demoUserStr);
            logger.log('🎭 [AUTH] Usuario demo recuperado de localStorage');
            setUser(demoUser);
          } else {
            setUser(null);
          }
        } catch (error) {
          logger.warn('⚠️ [AUTH] Error recuperando usuario demo:', error);
          setUser(null);
        }

        setIsLoading(false);
      }
    }, 10000);

    // Cleanup al desmontar: cancelar timeout y desuscribir del listener
    return () => {
      logger.log('🧹 [AUTH] Limpiando suscripción de autenticación');
      isMounted = false;
      if (initialCheckTimeout) clearTimeout(initialCheckTimeout);
      subscription.unsubscribe();
    };
  }, []); // Sin dependencias — solo ejecuta al montar el componente

  // =====================================================
  // LOGGING PARA DEBUGGING
  // =====================================================

  /**
   * Log del estado del usuario cada vez que cambia.
   * Facilita el debugging en DevTools sin necesidad de poner breakpoints.
   */
  useEffect(() => {
    if (user) {
      logger.log('👤 [AUTH] Usuario actual:', {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        organization: user.organization_id,
        unit: user.unit,
        isActive: user.is_active
      });
    }
  }, [user]);

  // =====================================================
  // VALOR DEL CONTEXTO
  // =====================================================

  const contextValue: AuthContextType = {
    user,
    isLoading,
    error,
    dbUserReady,
    isRecoveryMode,
    login,
    logout,
    clearError,
    refreshUser,
    updateProfile,
    updateMetadata,
    changePassword,
    resetPassword
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// =====================================================
// HOOKS PERSONALIZADOS
// =====================================================

/**
 * Hook principal para consumir el contexto de autenticación.
 * Lanza un error descriptivo si se usa fuera de `AuthProvider`.
 *
 * @returns El valor completo del `AuthContextType`
 * @throws Error si el hook se usa fuera del árbol del provider
 *
 * @example
 * const { user, login, logout, isLoading } = useAuth();
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}

/**
 * Hook de permisos basado en el rol del usuario autenticado.
 * Memoizado con `useMemo` para evitar recalcular en cada render cuando el usuario no cambia.
 *
 * ## Modelo de roles
 * | Rol                | Capacidades |
 * |--------------------|-------------|
 * | `admin`            | Todo — CRUD completo, gestión de usuarios, reportes |
 * | `technical_referent` | Editar controles y tareas asignados, subir evidencias |
 * | `auditor`          | Solo lectura + generación de reportes |
 * | `viewer`           | Solo lectura, sin reportes |
 *
 * @returns Objeto con booleanos de permisos y métodos de verificación
 *
 * @example
 * const { isAdmin, canEditControls, organizationId } = usePermissions();
 * if (!canEditControls) return <AccessDenied />;
 */
export function usePermissions() {
  const { user } = useAuth();

  return React.useMemo(() => ({
    /** true si el usuario tiene rol 'admin' */
    isAdmin: user?.role === 'admin',

    /** true si el usuario tiene rol 'technical_referent' */
    isTechnicalReferent: user?.role === 'technical_referent',

    /** true si el usuario tiene rol 'auditor' */
    isAuditor: user?.role === 'auditor',

    /** true si el usuario tiene rol 'viewer' (solo lectura) */
    isViewer: user?.role === 'viewer',

    /** Puede crear/editar/eliminar controles y tareas */
    canEditControls: user?.role === 'admin' || user?.role === 'technical_referent',

    /** Puede gestionar referentes técnicos y asignarlos a controles */
    canManageReferents: user?.role === 'admin',

    /** Puede ver y generar reportes de cumplimiento */
    canViewReports: user?.role === 'admin' || user?.role === 'auditor',

    /**
     * Verifica si el usuario pertenece a una organización específica.
     * @param organizationId - UUID de la organización a verificar
     */
    belongsToOrganization: (organizationId: string) => user?.organization_id === organizationId,

    /** UUID de la organización del usuario (para filtrar datos en otros contextos) */
    organizationId: user?.organization_id,

    /** Datos básicos del usuario para mostrar en UI (evita exponer datos sensibles) */
    userInfo: user ? {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      unit: user.unit
    } : null
  }), [user]);
}
